import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronRight } from "@material-symbols-svg/react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "src/redux/hooks";
import moment from "moment";

import DropdownV2 from "src/components/common/DropdownV2";
import InsightHistory from "./InsightHistory";
import InsightDataSources from "./InsightDataSources";
import PeriodNavigator, { PeriodSegment } from "./PeriodNavigator";
import BarChart, { BarChartDatum } from "../charts/BarChart";

import insightsApis from "src/api/insights";
import BcaApis from "src/api/bca";

/** Keys that can appear in BCA logger graph_data (for dropdown + chart) */
const BCA_GRAPH_KEYS = [
  "weight",
  "bmi",
  "bmr",
  "protein",
  "body_age",
  "fat_mass",
  "bone_mass",
  "hydration",
  "muscle_mass",
  "muscle_rate",
  "protein_rate",
  "visceral_fat",
  "lean_body_mass",
  "obesity_rating",
  "weight_control",
  "standard_weight",
  "body_fat_percentage",
  "subcutaneous_fat_percent",
] as const;

/** Fallback labels when BCA ranges API doesn't provide name */
const BCA_KEY_LABELS: Record<string, string> = {
  weight: "Weight",
  bmi: "BMI",
  bmr: "BMR",
  body_fat_percentage: "Body Fat %",
  muscle_mass: "Muscle Mass",
  muscle_rate: "Muscle Rate",
  bone_mass: "Bone Mass",
  hydration: "Hydration",
  protein: "Protein",
  protein_rate: "Protein Rate",
  visceral_fat: "Visceral Fat",
  body_age: "Body Age",
  fat_mass: "Fat Mass",
  lean_body_mass: "Lean Body Mass",
  obesity_rating: "Obesity Rating",
  weight_control: "Weight Control",
  standard_weight: "Standard Weight",
  subcutaneous_fat_percent: "Subcutaneous Fat %",
};

type RangesMap = Record<
  string,
  { name?: string; unit?: string; description?: string; ranges?: unknown[] }
>;

type FilterType = "day" | "week" | "month";

const TAB_TO_FILTER: Record<string, FilterType> = {
  daily: "day",
  weekly: "week",
  monthly: "month",
};

/** Format date as dd/mm/yyyy for API */
function toApiDate(m: moment.Moment): string {
  return m.format("DD/MM/YYYY");
}

/** Month 3-letter uppercase for API (e.g. JAN, DEC) */
function toApiMonth(m: moment.Moment): string {
  return m.format("MMM").toUpperCase();
}

type DropdownOption = { value: string; label: string };

/** API /gfc/loggers response graph_data item (full BCA object per row) */
interface GraphDataItem {
  date?: string;
  timestamp?: string;
  value?: string | number;
  weight?: number;
  bmi?: number;
  bmr?: number;
  [key: string]: unknown;
}

interface InsightComponentVitalProps {
  insightType: string;
  parameter: string;
  barColor?: string;
  icon?: React.ReactNode;
  supportProgramKey: string;
}

const InsightComponentVital: React.FC<InsightComponentVitalProps> = ({
  insightType,
  parameter,
  barColor,
  icon,
  supportProgramKey,
}) => {
  const navigate = useNavigate();
  const { patient } = useAppSelector((state) => state.auth);
  const [showHistory, setShowHistory] = useState(false);
  const [showDataSources, setShowDataSources] = useState(false);

  const [ranges, setRanges] = useState<RangesMap | null>(null);
  const [dropdownOptions, setDropdownOptions] = useState<DropdownOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<string>("weight");
  const [activeTab, setActiveTab] = useState<PeriodSegment>("daily");
  const [periodOffset, setPeriodOffset] = useState(0);
  const [graphData, setGraphData] = useState<GraphDataItem[]>([]);
  const [unitFromApi, setUnitFromApi] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    BcaApis.getBcaLifetronRanges({ queryParams: { deviceType: "GoodFlip" } })
      .then((res: any) => {
        if (cancelled) return;
        const code = res?.code != null ? Number(res.code) : null;
        if (code !== 1 && code !== 200) return;
        const data = res?.data as RangesMap | undefined;
        if (!data || typeof data !== "object") return;
        setRanges(data);
        const options: DropdownOption[] = BCA_GRAPH_KEYS.filter(
          (key) => data[key] != null || BCA_KEY_LABELS[key] != null
        ).map((key) => ({
          value: key,
          label: data[key]?.name ?? BCA_KEY_LABELS[key] ?? key,
        }));
        if (options.length > 0) setDropdownOptions(options);
        else
          setDropdownOptions(
            BCA_GRAPH_KEYS.map((key) => ({
              value: key,
              label: BCA_KEY_LABELS[key] ?? key,
            }))
          );
      })
      .catch(() => {
        if (!cancelled)
          setDropdownOptions(
            BCA_GRAPH_KEYS.map((key) => ({
              value: key,
              label: BCA_KEY_LABELS[key] ?? key,
            }))
          );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const buildQueryParams = useCallback(() => {
    const filterType = TAB_TO_FILTER[activeTab] ?? "day";
    const base: Record<string, string | number> = {
      user_id: patient?.data?.patient_id ?? "",
      logger: "bca",
      filter_type: filterType,
    };
    if (filterType === "day") {
      const date = moment().subtract(periodOffset, "day");
      base.date = toApiDate(date);
    } else if (filterType === "week") {
      const start = moment().subtract(periodOffset, "week").startOf("week");
      const end = start.clone().endOf("week");
      base.start_date = toApiDate(start);
      base.end_date = toApiDate(end);
    } else {
      const monthM = moment().subtract(periodOffset, "month").startOf("month");
      base.month = toApiMonth(monthM);
      base.year = monthM.year();
    }
    return base;
  }, [activeTab, periodOffset, patient?.data?.patient_id]);

  const fetchLogs = useCallback(async () => {
    const userId = patient?.data?.patient_id;
    if (!userId) {
      setGraphData([]);
      setUnitFromApi(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const queryParams = buildQueryParams();
      const res: any = await insightsApis.getLogData(queryParams);
      const code = res?.code != null ? Number(res.code) : null;
      if (code === -1 || code === 0) {
        setGraphData([]);
        setUnitFromApi(null);
        return;
      }
      const list = res?.data?.graph_data ?? [];
      setGraphData(Array.isArray(list) ? list : []);
      setUnitFromApi(res?.data?.unit ?? null);
    } catch {
      setGraphData([]);
      setUnitFromApi(null);
    } finally {
      setLoading(false);
    }
  }, [patient?.data?.patient_id, buildQueryParams]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const chartData = useMemo((): BarChartDatum[] => {
    const valueKey = selectedOption;
    const getValue = (item: GraphDataItem): number | null => {
      const raw = item[valueKey];
      if (raw == null) return null;
      const num = Number(raw);
      return Number.isFinite(num) ? num : null;
    };

    // Build valueByDate from API graph_data (shared by weekly and monthly)
    const valueByDate: Record<string, number> = {};
    graphData.forEach((item) => {
      const dateStr = item.date;
      if (dateStr) {
        const num = getValue(item);
        if (num != null) valueByDate[String(dateStr)] = num;
      }
    });

    // Weekly view: show all 7 days of the week, fill missing days with 0
    if (activeTab === "weekly") {
      const start = moment().subtract(periodOffset, "week").startOf("week");
      const result: BarChartDatum[] = [];
      for (let i = 0; i < 7; i++) {
        const d = start.clone().add(i, "day");
        const apiDate = d.format("DD/MM/YYYY");
        const value = valueByDate[apiDate] ?? 0;
        result.push({ label: d.format("D MMM"), value });
      }
      return result;
    }

    // Monthly view: show all days in the month, fill missing days with 0
    if (activeTab === "monthly") {
      const start = moment().subtract(periodOffset, "month").startOf("month");
      const daysInMonth = start.daysInMonth();
      const result: BarChartDatum[] = [];
      for (let i = 0; i < daysInMonth; i++) {
        const d = start.clone().add(i, "day");
        const apiDate = d.format("DD/MM/YYYY");
        const value = valueByDate[apiDate] ?? 0;
        result.push({ label: d.format("D MMM"), value });
      }
      return result;
    }

    // Daily: plot only API entries
    return [...graphData]
      .sort((a, b) => {
        const ta = a.timestamp
          ? new Date(a.timestamp).getTime()
          : a.date
          ? new Date(a.date.split("/").reverse().join("-")).getTime()
          : 0;
        const tb = b.timestamp
          ? new Date(b.timestamp).getTime()
          : b.date
          ? new Date(b.date.split("/").reverse().join("-")).getTime()
          : 0;
        return ta - tb;
      })
      .map((item): BarChartDatum | null => {
        const num = getValue(item);
        if (num == null) return null;
        let label: string = "—";
        if (item.date) {
          const parts = String(item.date).split("/");
          if (parts.length === 3) {
            const d = moment(
              `${parts[2]}-${parts[1]}-${parts[0]}`,
              "YYYY-MM-DD",
              true
            );
            label = d.isValid() ? d.format("D MMM") : String(item.date);
          } else label = String(item.date);
        } else if (
          item.timestamp &&
          moment(item.timestamp, "YYYY-MM-DD HH:mm:ss", true).isValid()
        ) {
          label = moment(item.timestamp).format("D MMM");
        }
        return { label, value: num };
      })
      .filter((d): d is BarChartDatum => d != null);
  }, [graphData, activeTab, periodOffset, selectedOption]);

  const unit = ranges?.[selectedOption]?.unit ?? unitFromApi ?? "kg";

  const handleConnectBca = () => {
    navigate(`/app/${supportProgramKey}/smart-scale-connection`);
  };

  if (showHistory) {
    return (
      <InsightHistory
        onBackClick={() => setShowHistory(false)}
        supportProgramKey={supportProgramKey}
        logger="bca"
        icon={icon}
      />
    );
  }

  if (showDataSources) {
    return <InsightDataSources onBackClick={() => setShowDataSources(false)} />;
  }

  // if (!loading && graphData.length === 0) {
  //   return (
  //     <div>
  //       <div className="px-4 space-y-4">
  //         <p className="text-sm text-gray-500">
  //           No data yet. Connect your BCA to start tracking.
  //         </p>
  //         <div className="rounded-xl border-2 border-dashed border-gray-200 bg-white p-8 flex flex-col items-center justify-center min-h-[220px]">
  //           <div
  //             className="rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center mb-3"
  //             style={{ width: 48, height: 48 }}
  //           >
  //             <BarChart2 className="w-6 h-6 text-gray-400" />
  //           </div>
  //           <p className="text-sm font-medium text-gray-500 text-center">
  //             No body composition entries
  //           </p>
  //         </div>
  //         <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-4">
  //           <div
  //             className="rounded-lg bg-amber-100 border border-amber-200 flex items-center justify-center flex-shrink-0"
  //             style={{ width: 48, height: 48 }}
  //           >
  //             <Scale className="w-6 h-6 text-gray-700" />
  //           </div>
  //           <div className="flex-1 min-w-0">
  //             <h3 className="font-semibold text-gray-900">Connect your BCA</h3>
  //             <p className="text-sm text-gray-500 mt-0.5">
  //               Enable body composition graph and trend insights
  //             </p>
  //           </div>
  //           <button
  //             type="button"
  //             onClick={handleConnectBca}
  //             className="flex-shrink-0 px-4 py-2.5 rounded-lg bg-[var(--app-primary)] text-white text-sm font-medium hover:opacity-90 transition-opacity"
  //           >
  //             Connect
  //           </button>
  //         </div>
  //       </div>
  //       <div className="p-4 space-y-3">
  //         <button
  //           type="button"
  //           onClick={() => setShowHistory(true)}
  //           className="w-full bg-white rounded-xl px-4 py-3 flex items-center justify-between shadow-sm border border-gray-100"
  //         >
  //           <span>Show All Data</span>
  //           <ChevronRight className="w-6 h-6" />
  //         </button>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div>
      <div className="px-4 space-y-4">
        <DropdownV2
          options={
            dropdownOptions.length > 0
              ? dropdownOptions
              : [{ label: "Weight", value: "weight" }]
          }
          value={selectedOption}
          onChange={(value) => setSelectedOption(value as string)}
        />

        <div className="bg-white rounded-xl p-4 space-y-4">
          <PeriodNavigator
            activeTab={activeTab}
            periodOffset={periodOffset}
            onTabChange={(tab) => {
              setActiveTab(tab);
              setPeriodOffset(0);
            }}
            onPrev={() => setPeriodOffset((p) => p + 1)}
            onNext={() => setPeriodOffset((p) => Math.max(0, p - 1))}
          />

          {loading ? (
            <div className="h-40 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-[var(--app-primary)] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-40 flex items-center justify-center text-sm text-gray-500">
              No data for this period
            </div>
          ) : (
            <BarChart
              data={chartData}
              unit={unit}
              activeIndex={activeIndex}
              barColor={barColor}
              onBarClick={(index) => setActiveIndex(index)}
            />
          )}
        </div>
      </div>

      <div className="p-4 space-y-3">
        <button
          type="button"
          onClick={() => setShowHistory(true)}
          className="w-full bg-white rounded-xl px-4 py-3 flex items-center justify-between shadow-sm border border-gray-100"
        >
          <span>Show All Data</span>
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>
    </div>
  );
};

export default InsightComponentVital;
